import { CONTENT_URL } from 'src/api';
import { Button } from '../Buttons/Button';
import { useTranslation } from 'react-i18next';
import { LocalTournament, useApiContext, useApplyTournament } from 'react-easyrocket';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useModalContext } from 'src/hooks/useModalContext';
import { tournamentsStorageApi } from 'src/modules/localTournamentNotification/storageApi';
import { useNavigate } from 'react-router';
import { useMessageContext } from 'src/context/useMessage';
import { PrizePoolTournament } from '../Tournaments/PrizePoolTournament';
import { ModalWrapper } from '../ModalWrapper';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

export const NewLocalTournament = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useApiContext();
  const { modalProps, closeModal, openModal } = useModalContext<LocalTournament>();
  const { mutateAsync: participateInLocalTournament, isLoading } = useApplyTournament();
  const QueryClient = useQueryClient();
  const { closeMessage } = useMessageContext();
  const { startRegistration } = useAuthFlow();

  useEffect(() => {
    tournamentsStorageApi.markAsShown(modalProps.id);
  }, [modalProps.id]);

  const handleClose = () => {
    tournamentsStorageApi.markAsClosed(modalProps.id);
  };

  const handleParticipateClick = useCallback(async () => {
    if (isAuthenticated) {
      await participateInLocalTournament(
        { tournament_id: modalProps.id },
        {
          onError: (error) => {
            if (error.response?.data && error.response?.data?.code === 16) {
              closeModal();
              closeMessage?.(error.response.data.message || '');
              openModal('TOURNAMENT_LOW_LEVEL');
            }
          },
          onSuccess: () => {
            tournamentsStorageApi.updateTournamentHistory(Number(modalProps.id), {
              isClosed: true
            });
          }
        }
      );
      closeModal();
    } else {
      closeModal();
      startRegistration({
        onSuccess: async () => {
          await participateInLocalTournament(
            { tournament_id: modalProps.id },
            {
              onError: (error) => {
                if (error.response?.data && error.response?.data?.code === 16) {
                  closeMessage?.(error.response.data.message || '');
                  openModal('TOURNAMENT_LOW_LEVEL');
                }
              },
              onSuccess: () => {
                tournamentsStorageApi.updateTournamentHistory(Number(modalProps.id), {
                  isClosed: true
                });
              }
            }
          );
          QueryClient.invalidateQueries(['tournament-with-users-progress']);
        }
      });
    }
    tournamentsStorageApi.markAsClosed(modalProps.id);
    navigate(`/tournaments/local/${modalProps.id}?participating=true`);
  }, [
    QueryClient,
    closeMessage,
    closeModal,
    isAuthenticated,
    modalProps.id,
    navigate,
    openModal,
    participateInLocalTournament
  ]);

  const handleShowMoreClick = useCallback(() => {
    tournamentsStorageApi.markAsClosed(modalProps.id);
    navigate(`/tournaments/local/${modalProps.id}`);
    closeModal();
  }, [closeModal, modalProps.id, navigate]);

  return (
    <ModalWrapper onClose={handleClose}>
      <div className="max-w-[400px]">
        <img
          src={`${CONTENT_URL}${modalProps.imageMobile}`}
          className="z-0 relative max-h-[250px]"
          alt="new tournament image"
        />
        <div className="p-[20px] bg-mg_bg z-[1] relative">
          <p className="text-center text-mg_text_label uppercase text-[20px] font-bold mb-[5px] leading-[110%]">
            {modalProps?.name}
          </p>
          <p className="text-center text-mg_text_label text-[14px] font-medium leading-[110%]">
            {t('general.prizePool')}:
          </p>
          <div className="text-center text-mg_text text-[26px] font-black leading-[100%] mb-[15px]">
            <PrizePoolTournament tournament={modalProps} className="justify-center" />
          </div>
          <p className="text-center text-mg_text text-[14px] font-bold mb-[15px]">
            {modalProps?.descriptionShort}
          </p>
          <Button
            loading={isLoading}
            title={t('general.participate') as string}
            label={t('general.participate') as string}
            mode="button-secondary"
            className="!w-full !max-w-[400px] mb-[15px]"
            onClick={handleParticipateClick}
          />
          <div className="flex gap-3">
            <Button
              title={t('general.cancel') as string}
              label={t('general.cancel') as string}
              mode="button-secondary"
              className="m-auto"
              onClick={() => {
                closeModal();
              }}
            />
            <Button
              title={t('general.readMore') as string}
              label={t('general.readMore') as string}
              mode="button-primary"
              className="m-auto"
              onClick={handleShowMoreClick}
            />
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
